import axiosClient from "./axiosClient";

const unwrap = response =>
  response?.data?.data ?? response?.data?.payload ?? response?.data ?? {};

const collection = response => {
  const value = unwrap(response);
  return Array.isArray(value) ? value : value.items ?? value.data ?? [];
};

const quiet = { __silentGenericToast: true };

const dateOnlyPattern = /^(\d{4})-(\d{2})-(\d{2})$/;
const localTimePattern = /^([01]\d|2[0-3]):([0-5]\d)$/;

export function normalizeDateOnly(value, fieldName = "Date") {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value !== "string") {
    throw new Error(`${fieldName} must use yyyy-MM-dd.`);
  }

  const match = dateOnlyPattern.exec(value);
  if (!match) throw new Error(`${fieldName} must use yyyy-MM-dd.`);

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const daysInMonth = [31, leapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  if (month < 1 || month > 12 || day < 1 || day > daysInMonth[month - 1]) {
    throw new Error(`${fieldName} must be a valid calendar date.`);
  }
  return value;
}

export function normalizeLocalTime(value, fieldName = "Time") {
  if (typeof value !== "string" || !localTimePattern.test(value)) {
    throw new Error(`${fieldName} must use 24-hour HH:mm.`);
  }
  return `${value}:00`;
}

export function normalizeAvailabilityRuleRequest(body = {}) {
  const effectiveFromDate = normalizeDateOnly(body.effectiveFromDate, "Effective From");
  const effectiveToDate = normalizeDateOnly(body.effectiveToDate, "Effective To");
  const startLocalTime = normalizeLocalTime(body.startLocalTime, "Start time");
  const endLocalTime = normalizeLocalTime(body.endLocalTime, "End time");

  if (startLocalTime >= endLocalTime) {
    throw new Error("End time must be after start time.");
  }
  if (effectiveFromDate && effectiveToDate && effectiveToDate < effectiveFromDate) {
    throw new Error("Effective To cannot be before Effective From.");
  }

  return {
    ...body,
    startLocalTime,
    endLocalTime,
    effectiveFromDate,
    effectiveToDate,
  };
}

export async function listSchedulingOfferings(params = {}) {
  return collection(await axiosClient.get("/scheduling/offerings", {
    params: { page: 1, pageSize: 100, ...params }, ...quiet,
  }));
}

async function listSchedulingCollection(path) {
  const response = await axiosClient.get(path, {
    params: { page: 1, pageSize: 100 },
    __silentGenericToast: true,
  });
  const envelope = response?.data?.payload ?? response?.data?.data ?? response?.data ?? {};
  return Array.isArray(envelope) ? envelope : envelope.items ?? envelope.data ?? [];
}

export const listSchedulingResources = () => listSchedulingCollection("/scheduling/resources");
export const listSchedulingServiceAreas = () => listSchedulingCollection("/scheduling/service-areas");

export const getSchedulingSettings = async () => unwrap(await axiosClient.get("/scheduling/settings", quiet));
export const updateSchedulingSettings = async body => unwrap(await axiosClient.put("/scheduling/settings", body));
export const getSchedulingTimeZone = async () => unwrap(await axiosClient.get("/scheduling/timezone", quiet));
export const updateSchedulingTimeZone = async body => unwrap(await axiosClient.put("/scheduling/timezone", body));

export const listSchedulingBookings = async params => unwrap(await axiosClient.get("/scheduling/bookings", { params, ...quiet }));
export const getSchedulingBooking = async id => unwrap(await axiosClient.get(`/scheduling/bookings/${id}`, quiet));
export const createManualSchedulingBooking = async body => unwrap(await axiosClient.post("/scheduling/bookings/manual", body));
export const cancelSchedulingBooking = async (id, body) => unwrap(await axiosClient.post(`/scheduling/bookings/${id}/cancel`, body));
export const completeSchedulingBooking = async (id, expectedVersion) => unwrap(await axiosClient.post(`/scheduling/bookings/${id}/complete`, { expectedVersion }));
export const markSchedulingBookingNoShow = async (id, expectedVersion) => unwrap(await axiosClient.post(`/scheduling/bookings/${id}/no-show`, { expectedVersion }));

export const createSchedulingOffering = async body => unwrap(await axiosClient.post("/scheduling/offerings", body));
export const updateSchedulingOffering = async (id, body) => unwrap(await axiosClient.put(`/scheduling/offerings/${id}`, body));
export const setSchedulingOfferingActive = async (id, isActive, expectedVersion) => unwrap(await axiosClient.put(`/scheduling/offerings/${id}/active`, { isActive, expectedVersion }));
export const createSchedulingResource = async body => unwrap(await axiosClient.post("/scheduling/resources", body));
export const updateSchedulingResource = async (id, body) => unwrap(await axiosClient.put(`/scheduling/resources/${id}`, body));
export const setSchedulingResourceActive = async (id, isActive, expectedVersion) => unwrap(await axiosClient.put(`/scheduling/resources/${id}/active`, { isActive, expectedVersion }));
export const createSchedulingServiceArea = async body => unwrap(await axiosClient.post("/scheduling/service-areas", body));
export const updateSchedulingServiceArea = async (id, body) => unwrap(await axiosClient.put(`/scheduling/service-areas/${id}`, body));
export const setSchedulingServiceAreaActive = async (id, isActive, expectedVersion) => unwrap(await axiosClient.put(`/scheduling/service-areas/${id}/active`, { isActive, expectedVersion }));

export const listOfferingResources = async offeringId => collection(await axiosClient.get(`/scheduling/offerings/${offeringId}/resources`, quiet));
export const setOfferingResource = async (offeringId, resourceId, body) => unwrap(await axiosClient.put(`/scheduling/offerings/${offeringId}/resources/${resourceId}`, body));
export const listResourceServiceAreas = async resourceId => collection(await axiosClient.get(`/scheduling/resources/${resourceId}/service-areas`, quiet));
export const setResourceServiceArea = async (resourceId, areaId, body) => unwrap(await axiosClient.put(`/scheduling/resources/${resourceId}/service-areas/${areaId}`, body));
export const listAvailabilityRules = async resourceId => collection(await axiosClient.get(`/scheduling/resources/${resourceId}/availability-rules`, quiet));
export const createAvailabilityRule = async (resourceId, body) => unwrap(await axiosClient.post(`/scheduling/resources/${resourceId}/availability-rules`, normalizeAvailabilityRuleRequest(body)));
export const updateAvailabilityRule = async (resourceId, ruleId, body) => unwrap(await axiosClient.put(`/scheduling/resources/${resourceId}/availability-rules/${ruleId}`, normalizeAvailabilityRuleRequest(body)));
export const setAvailabilityRuleActive = async (resourceId, ruleId, isActive, expectedVersion) => unwrap(await axiosClient.put(`/scheduling/resources/${resourceId}/availability-rules/${ruleId}/active`, { isActive, expectedVersion }));
export const listAvailabilityExceptions = async (resourceId, params = {}) => collection(await axiosClient.get(`/scheduling/resources/${resourceId}/availability-exceptions`, { params, ...quiet }));
export const createAvailabilityException = async (resourceId, body) => unwrap(await axiosClient.post(`/scheduling/resources/${resourceId}/availability-exceptions`, body));
export const setAvailabilityExceptionActive = async (resourceId, exceptionId, isActive, expectedVersion) => unwrap(await axiosClient.put(`/scheduling/resources/${resourceId}/availability-exceptions/${exceptionId}/active`, { isActive, expectedVersion }));

export const listSchedulingContacts = async search => collection(await axiosClient.get("/contacts/", { params: { page: 1, pageSize: 50, search }, ...quiet }));
export const listSchedulingBookingContacts = async search => collection(await axiosClient.get("/scheduling/booking-contacts", { params: { search }, ...quiet }));
